import { useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { FindInvoiceResponse, InvoiceDetails } from '@/api/invoices';
import { formatLocationAddressShort } from '@/api/locations';
import TwoColumnCard from '@/components/TwoColumnCard';
import TwoColumnForm from '@/components/TwoColumnForm';
import FindInvoiceForm from '@/features/invoices/FindInvoiceForm';
import AuthActionsCard from '@/features/payment/AuthActionsCard';
import BillingDetailsCard from '@/features/payment/BillingDetailsCard';
import ConfirmAmountCard from '@/features/payment/ConfirmAmountCard';
import PaymentHistoryCard from '@/features/payment/PaymentHistoryCard';
import usePaymentCardForm from '@/hooks/usePaymentCardForm';
import { formatDate } from '@/utils/dates';

export default function PayInvoicePage() {
  const loaderData = useLoaderData<FindInvoiceResponse>();

  if (!loaderData.invoice) {
    return (
      <FindInvoiceForm
        title="Pay Invoice"
        subtitle="Let's find your invoice! Enter some details from the invoice you'd like to pay."
        method="GET"
        actionLabel="Find Invoice"
        cancelRoute="/"
        showAuthActions
      />
    );
  } else {
    return <PayInvoiceForm invoice={loaderData.invoice} />;
  }
}

interface InvoiceFormProps {
  readonly invoice: InvoiceDetails;
}

function PayInvoiceForm({ invoice }: InvoiceFormProps) {
  const {
    paymentCard,
    cardError,
    isBusy,
    handleSubmit: handleCardSubmit,
  } = usePaymentCardForm(invoice.publicKey);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [showInvoicePreviewError, setShowInvoicePreviewError] = useState(false);
  const [activeTab, setActiveTab] = useState<'card' | 'ach'>('card');

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    if (activeTab === 'card') {
      await handleCardSubmit(event);
    } else {
      // For ACH payments, just let the form submit normally
      // The action handler will process the ACH data
    }
  }

  return (
    <TwoColumnForm
      title="Pay Invoice"
      subtitle={formatInvoiceTitle(invoice)}
      onSubmit={(event) => void handleSubmit(event)}
      leftColumn={
        <>
          {invoice.payments.length > 0 && (
            <PaymentHistoryCard invoice={invoice} />
          )}

          {invoice.balanceDue > 0 && (
            <>
              <ConfirmAmountCard invoice={invoice} />
              <BillingDetailsCard
                paymentCard={paymentCard}
                cardError={cardError}
                isBusy={isBusy}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                achEnabled={invoice.achEnabled}
              />
            </>
          )}
        </>
      }
      rightColumn={
        <>
          <TwoColumnCard title="Invoice Details">
            <div
              className="bg-light flex-column justify-content-center align-items-center text-secondary fs-5"
              style={{
                aspectRatio: '608/392',
                display: showInvoicePreview ? 'none' : 'flex',
              }}
            >
              {showInvoicePreviewError && <p>Unable to load invoice details</p>}
            </div>

            <a
              href={invoice.pdfLink}
              target="_blank"
              rel="noreferrer"
              style={{ display: showInvoicePreview ? 'block' : 'none' }}
            >
              <img
                src={invoice.pngLink}
                alt="Invoice PDF"
                className="d-block w-100"
                onLoad={() => setShowInvoicePreview(true)}
                onError={() => setShowInvoicePreviewError(true)}
              />
            </a>
          </TwoColumnCard>
          <AuthActionsCard />
        </>
      }
    />
  );
}

function formatInvoiceTitle(invoice: InvoiceDetails) {
  let title = `Work Order #${invoice.workOrderNumber}`;

  if (invoice.completedDate) {
    title += ` on ${formatDate(invoice.completedDate)}`;
  }

  title += ` at ${formatLocationAddressShort(invoice)}`;

  return title;
}
